/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {EmailTranslationApiService, EmailTranslationModel} from './email-translation-api.service';
import {EmailTranslationEditPageComponent} from './email-translation-edit-page.component';
import {EmailTranslationRoutePageComponent} from './email-translation-route-page.component';

export function emailTranslationStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(EmailTranslationApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.politicalAdmin.apiKey, null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper(
    'email-translations.email-translation',
    EmailTranslationApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {
        component: EmailTranslationRoutePageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());

  states.add(rh.itemPath(), rh.item({
    resolve: {
      emailTranslationPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: EmailTranslationModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            applications: [],
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Email Translation');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Email Translation'));
      },
      model: function(emailTranslationPage: SignalAdminResourcePageState<EmailTranslationModel>) {
        return emailTranslationPage.model();
      }
    },
    views: {
      'content@app': {
        component: EmailTranslationEditPageComponent,
        bindings: {state: 'emailTranslationPage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'emailTranslationPage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'emailTranslationPage'}
      }
    }
  }));

  return states.build();
}
